import { DecisionKindEnum } from "@/enum/decision-kind";
import { PayrollVoucherStatusEnum } from "@/enum/payroll-voucher-status";

import { isDiscountableTotal } from "../installments";
import { PayrollVoucher } from "../types";

export const VOUCHER_SKIP_REASONS = {
  ALREADY_DECIDED: "ALREADY_DECIDED",
  NOT_DISCOUNTABLE: "NOT_DISCOUNTABLE",
} as const;

export type VoucherSkipReason =
  (typeof VOUCHER_SKIP_REASONS)[keyof typeof VOUCHER_SKIP_REASONS];

// Espelho de VOUCHER_DECISION_SKIP_REASONS da API: o pre-flight local nao preve reversao nem corrida de version.
export const VOUCHER_API_SKIP_REASONS = {
  INVALID_DECISION: "INVALID_DECISION",
  REJECTION_REASON_REQUIRED: "REJECTION_REASON_REQUIRED",
  NOT_FOUND: "NOT_FOUND",
  REVERTED: "REVERTED",
  ALREADY_DECIDED: "ALREADY_DECIDED",
  INSTALLMENTS_OUT_OF_RANGE: "INSTALLMENTS_OUT_OF_RANGE",
  INSTALLMENT_NOT_POSITIVE: "INSTALLMENT_NOT_POSITIVE",
  VERSION_CONFLICT: "VERSION_CONFLICT",
} as const;

export type VoucherApiSkipReason =
  (typeof VOUCHER_API_SKIP_REASONS)[keyof typeof VOUCHER_API_SKIP_REASONS];

export const UNKNOWN_SKIP_LABEL: string = "Não pôde ser decidido";

export const VOUCHER_SKIP_LABELS: Record<
  VoucherApiSkipReason | VoucherSkipReason,
  string
> = {
  [VOUCHER_SKIP_REASONS.ALREADY_DECIDED]: "Já decidido",
  [VOUCHER_SKIP_REASONS.NOT_DISCOUNTABLE]: "Sem valor a descontar",
  [VOUCHER_API_SKIP_REASONS.INVALID_DECISION]: "Decisão inválida",
  [VOUCHER_API_SKIP_REASONS.REJECTION_REASON_REQUIRED]:
    "Sem motivo de reprovação",
  [VOUCHER_API_SKIP_REASONS.NOT_FOUND]: "Vale não encontrado",
  [VOUCHER_API_SKIP_REASONS.REVERTED]: "Vale revertido",
  [VOUCHER_API_SKIP_REASONS.INSTALLMENTS_OUT_OF_RANGE]:
    "Parcelamento inválido — peça ao Gerente para revisar",
  [VOUCHER_API_SKIP_REASONS.INSTALLMENT_NOT_POSITIVE]: "Sem valor a descontar",
  [VOUCHER_API_SKIP_REASONS.VERSION_CONFLICT]: "Decidido por outra pessoa",
};

export const skipLabelOf = (reason: string): string =>
  VOUCHER_SKIP_LABELS[reason as VoucherApiSkipReason] ?? UNKNOWN_SKIP_LABEL;

export interface VoucherSkip {
  voucher: PayrollVoucher;
  reason: VoucherSkipReason;
}

export interface VoucherBatchPreflight {
  valid: PayrollVoucher[];
  skipped: VoucherSkip[];
}

const skipReasonOf = (
  voucher: PayrollVoucher,
  kind: DecisionKindEnum,
): VoucherSkipReason | null => {
  if (voucher.status !== PayrollVoucherStatusEnum.PENDING) {
    return VOUCHER_SKIP_REASONS.ALREADY_DECIDED;
  }

  if (kind !== DecisionKindEnum.APPROVE) return null;

  if (!isDiscountableTotal(voucher.amount)) {
    return VOUCHER_SKIP_REASONS.NOT_DISCOUNTABLE;
  }

  return null;
};

export const buildVoucherPreflight = (
  vouchers: readonly PayrollVoucher[],
  kind: DecisionKindEnum,
): VoucherBatchPreflight => {
  const valid: PayrollVoucher[] = [];
  const skipped: VoucherSkip[] = [];

  vouchers.forEach((voucher: PayrollVoucher): void => {
    const reason: VoucherSkipReason | null = skipReasonOf(voucher, kind);

    if (!reason) {
      valid.push(voucher);
      return;
    }

    skipped.push({ voucher, reason });
  });

  return { valid, skipped };
};

export const summarizeSkipped = (
  skipped: readonly { reason: string }[],
): string => {
  const byReason: Map<string, number> = new Map<string, number>();

  skipped.forEach((item: { reason: string }): void => {
    byReason.set(item.reason, (byReason.get(item.reason) ?? 0) + 1);
  });

  return Array.from(byReason.entries())
    .map(
      ([reason, count]: [string, number]): string =>
        `${count} ${skipLabelOf(reason)}`,
    )
    .join(" · ");
};
